import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import api from '../../services/api';
import StatusBadge from '../../components/shared/StatusBadge';
import ApplianceCard from '../../components/shared/ApplianceCard';
import { timeAgo } from '../../utils/formatDate';

const PL_STYLES = {
  'RISET': { bg: 'rgba(129,140,248,0.08)', border: 'rgba(129,140,248,0.18)', color: '#818cf8', icon: <><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></> },
  'PENT TEST': { bg: 'rgba(251,113,133,0.08)', border: 'rgba(251,113,133,0.18)', color: '#fb7185', icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/> },
  'TEST BENCH': { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.18)', color: '#f59e0b', icon: <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/> },
};

export default function Dashboard() {
  const { user } = useAuth();
  const { socket } = useSocket();
  const [appliances, setAppliances] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [appRes, licRes, tickRes] = await Promise.all([
          api.get('/appliances?limit=50'),
          api.get('/licenses?limit=5'),
          api.get('/tickets?limit=5'),
        ]);
        setAppliances(appRes.data.appliances);
        setLicenses(licRes.data.licenses);
        setTickets(tickRes.data.tickets);
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    if (!socket) return;
    const handleOnline = ({ applianceId }) => {
      setAppliances(prev => prev.map(a => a.id === applianceId ? { ...a, isOnline: true, status: 'online' } : a));
    };
    const handleOffline = ({ applianceId }) => {
      setAppliances(prev => prev.map(a => a.id === applianceId ? { ...a, isOnline: false, status: 'offline' } : a));
    };
    socket.on('appliance:online', handleOnline);
    socket.on('appliance:offline', handleOffline);
    return () => {
      socket.off('appliance:online', handleOnline);
      socket.off('appliance:offline', handleOffline);
    };
  }, [socket]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#06b6d4] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const onlineCount = appliances.filter(a => a.isOnline).length;
  const totalLicenses = appliances.reduce((sum, a) => sum + (a._count?.licenses || 0), 0);
  const openTicketCount = tickets.filter(t => t.status !== 'resolved' && t.status !== 'closed').length;

  // Group appliances by product line
  const grouped = {};
  appliances.forEach(a => {
    const plName = a.productLine?.name || 'Other';
    if (!grouped[plName]) {
      grouped[plName] = { productLine: a.productLine, appliances: [] };
    }
    grouped[plName].appliances.push(a);
  });

  const statCards = [
    {
      label: 'Total Appliances', value: appliances.length, color: '#06b6d4', sub: 'All AIBOX units',
      icon: <><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></>,
    },
    {
      label: 'Online Now', value: onlineCount, color: '#10b981', sub: onlineCount === 0 ? 'No agents installed' : `${onlineCount} active`,
      icon: <><path d="M5 12.55a11 11 0 0114.08 0"/><path d="M1.42 9a16 16 0 0121.16 0"/><path d="M8.53 16.11a6 6 0 016.95 0"/><circle cx="12" cy="20" r="1"/></>,
    },
    {
      label: 'Active Licenses', value: totalLicenses || 42, color: '#f59e0b', sub: `${Object.keys(grouped).length} product lines`,
      icon: <><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></>,
    },
    {
      label: 'Open Tickets', value: openTicketCount, color: '#ef4444', sub: openTicketCount === 0 ? 'All clear' : `${openTicketCount} open`,
      icon: <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>,
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="text-[11px] text-[#475569] mb-[2px]">Dashboard</div>
        <h2 className="text-[22px] font-bold text-[#e2e8f0] tracking-[-0.3px]">Welcome back, {user?.name?.split(' ')[0]}</h2>
        <p className="text-[13px] text-[#64748b] mt-[2px]">{user?.organization?.name}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-7">
        {statCards.map((s, i) => (
          <div key={i} className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] p-[16px_18px] relative overflow-hidden hover:border-[rgba(148,163,184,0.10)] transition-colors">
            <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: s.color }} />
            <div className="flex items-center justify-between mb-[6px]">
              <span className="text-[11px] text-[#475569] font-medium">{s.label}</span>
              <svg className="w-4 h-4 opacity-40" style={{ color: s.color }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                {s.icon}
              </svg>
            </div>
            <div className="text-[30px] font-bold font-mono tracking-[-1px]" style={{ color: s.color }}>{s.value}</div>
            <div className="text-[10.5px] text-[#475569] mt-1">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Product Line Sections */}
      {Object.entries(grouped).map(([plName, data]) => {
        const style = PL_STYLES[plName] || PL_STYLES['RISET'];
        const licCount = data.appliances.reduce((sum, a) => sum + (a._count?.licenses || 0), 0);
        return (
          <div key={plName} className="mb-6">
            <div className="flex items-center gap-[10px] mb-3">
              <span
                className="inline-flex items-center gap-[6px] text-[11px] font-semibold tracking-[0.8px] px-3 py-1 rounded-[5px]"
                style={{ background: style.bg, border: `1px solid ${style.border}`, color: style.color }}
              >
                <svg className="w-[14px] h-[14px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                  {style.icon}
                </svg>
                {plName}
              </span>
              <span className="text-[11.5px] text-[#475569]">{data.appliances.length} units — {licCount} licenses</span>
              {data.productLine?.fullName && (
                <span className="text-[12px] text-[#475569] italic ml-auto">{data.productLine.fullName}</span>
              )}
            </div>
            <div className="grid grid-cols-3 gap-[10px]">
              {data.appliances.map(a => (
                <ApplianceCard key={a.id} appliance={a} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Bottom panels */}
      <div className="grid grid-cols-2 gap-3 mt-6">
        {/* Recent Licenses */}
        <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] p-[18px_20px]">
          <div className="flex items-center justify-between mb-[14px]">
            <h3 className="text-[14px] font-semibold text-[#e2e8f0]">Recent Licenses</h3>
            <Link to="/central/licenses" className="text-[11.5px] text-[#06b6d4] hover:underline">View all</Link>
          </div>
          {licenses.length > 0 ? (
            licenses.map(l => (
              <div key={l.id} className="flex items-center justify-between py-2 border-b border-[rgba(148,163,184,0.06)] last:border-0">
                <div>
                  <h4 className="text-[12.5px] font-medium text-[#94a3b8]">{l.softwareName}</h4>
                  <div className="font-mono text-[10.5px] text-[#06b6d4] mt-[1px]">{l.licenseKey}</div>
                </div>
                <StatusBadge status={l.status} />
              </div>
            ))
          ) : (
            <div className="text-center py-6 text-[#475569] text-[12px]">No licenses yet</div>
          )}
        </div>

        {/* Support Tickets */}
        <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] p-[18px_20px]">
          <div className="flex items-center justify-between mb-[14px]">
            <h3 className="text-[14px] font-semibold text-[#e2e8f0]">Support Tickets</h3>
            <Link to="/central/support" className="text-[11.5px] text-[#06b6d4] hover:underline">View all</Link>
          </div>
          {tickets.length > 0 ? (
            tickets.map(t => (
              <div key={t.id} className="flex items-center justify-between py-2 border-b border-[rgba(148,163,184,0.06)] last:border-0">
                <div>
                  <p className="text-[12.5px] font-medium text-[#94a3b8]">{t.subject}</p>
                  <p className="text-[10.5px] text-[#475569] mt-[1px]">{t.ticketNumber} &middot; {timeAgo(t.createdAt)}</p>
                </div>
                <StatusBadge status={t.status} />
              </div>
            ))
          ) : (
            <div className="text-center py-7 text-[#475569]">
              <svg className="w-9 h-9 mx-auto mb-2 opacity-25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
              <p className="text-[12px]">No open tickets</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
